import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Play, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

// Read-only rule preview. Hits POST /assignment-rules/{id}/preview.
// No writes. Helps admins understand who would currently satisfy a rule.

export default function RulePreviewDialog({ open, onOpenChange, rule, skills }) {
  const [onDate, setOnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setOnDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const skillName = (sid) => (skills.find((s) => s.skill_id === sid) || {}).name || sid;

  const run = async () => {
    if (!rule) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/assignment-rules/${rule.rule_id}/preview`,
        { on_date: onDate, employee_ids: null });
      setResult(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  if (!rule) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" fullscreenOnMobile data-testid="rule-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview: {rule.name}</DialogTitle>
          <DialogDescription>
            Read-only dry-run. Shows which active employees would currently satisfy the
            rule's eligibility on the chosen date. Nothing is written.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3 mb-3">
          <div className="flex-1">
            <Label className="eyebrow">As of date</Label>
            <Input type="date" value={onDate}
              data-testid="rule-preview-date"
              onChange={(e) => setOnDate(e.target.value)}
              className="h-10 rounded-lg mt-2" />
          </div>
          <Button type="button" onClick={run} disabled={busy}
            data-testid="rule-preview-run"
            className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Play className="h-4 w-4 mr-1" />{busy ? "Running…" : "Run preview"}
          </Button>
        </div>

        {result && (
          <div data-testid="rule-preview-result">
            <div className="flex gap-2 mb-3 text-xs">
              <span data-testid="preview-eligible-count"
                    className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                {result.summary.eligible} eligible
              </span>
              <span data-testid="preview-blocked-count"
                    className="px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                {result.summary.blocked} blocked
              </span>
              <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {result.summary.total} total
              </span>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F9FE] sticky top-0">
                  <tr className="text-left text-[#4B5773]">
                    <th className="py-2 px-3 eyebrow font-normal">Employee</th>
                    <th className="py-2 px-3 eyebrow font-normal">Status</th>
                    <th className="py-2 px-3 eyebrow font-normal">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {result.results.length === 0 && (
                    <tr><td colSpan={3} className="py-6 text-center text-[#4B5773]"
                            data-testid="rule-preview-empty">
                      No active employees in scope.
                    </td></tr>
                  )}
                  {result.results.map((row) => (
                    <tr key={row.employee_id}
                        data-testid={`preview-row-${row.employee_id}`}>
                      <td className="py-2 px-3 font-medium text-[#0B1A36]">{row.employee_name}</td>
                      <td className="py-2 px-3">
                        {row.eligible ? (
                          <span className="inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Eligible
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
                            <XCircle className="h-3 w-3 mr-1" />Blocked
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-[#4B5773]">
                        {row.blocking_reasons.map((b, i) => (
                          <div key={`b-${i}`} className="text-red-700">
                            • {b.message} ({skillName(b.skill_id)})
                          </div>
                        ))}
                        {row.warnings.map((w, i) => (
                          <div key={`w-${i}`} className="text-amber-700 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {w.message} ({skillName(w.skill_id)})
                          </div>
                        ))}
                        {row.eligible && row.warnings.length === 0 && (
                          <span className="text-emerald-700">All required skills met.</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
